import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { FormFieldSelect } from '../../components/Form/FormField';
import { useForm } from 'react-hook-form';
import { PageBody } from '../../components/SiteWrapper';
import * as MinsitryReportApi from '../../api/MinistryReportApi';
import * as MinistryExamApi from '../../api/MinistryExams';
import moment from 'moment';
import { toast } from 'react-toastify';
import DataTable from '../../components/DataTable';
import { useRowSpan } from '../../components/DataTable/useRowSpan';

const TimeTracker = () => {
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [exams, setExams] = useState([]);
  const [examDays, setExamDays] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const categoryColors = {
    examdetails: 'rgb(234,24,38',
    drivedownload: 'rgb(254, 195, 190)',
    contentdelivery: 'rgb(224,224,224)',
    contentprocess: 'rgb(255,224,143)',
    entryofcandidates: 'rgb(203,235,174)',
    registration: 'rgb(179,218,244)',
    aadharauthentication: 'rgb(187,211,218)',
    examcommencement: 'rgb(223,194,238)',
    allowedcandidateslogin: 'rgb(121,159,229)',
    examcompletion: 'rgb(144,132,219)',
    lockunlockincidents: 'rgb(122,234,203)',
  };

  const shiftColors = {
    'shift-1': 'rgb(203,235,174)',
    'shift-2': 'rgb(179,218,244)',
    'shift-3': 'rgb(255,224,143)',
  };

  const {
    control,
    formState: { errors: timeTrackerError },
    watch,
    setValue,
  } = useForm({
    mode: 'onChange',
  });
  const [filter, setFilter] = useState({
    _page: 1,
    _limit: 25,
    q: '',
    _sort: '',
    _order: 'desc',
    shift: '',
    examDay: '',
    selectedExam: '',
  });

  const initialState = {
    sortBy: [{ id: 'id', desc: false }],
    pageSize: filter._limit,
    pageIndex: 0,
  };

  const selectedExamDay = watch('examDay');
  const selectedShift = watch('shift');
  const selectedExam = watch('exam');

  const getTimeTrackerData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await MinsitryReportApi.getTimeTrackerData(filter);
      if (response.success) {
        setData(response?.data?.flatTrackerList);
        setTotalRecords(response?.data?.totalRows);
      } else {
        toast.error(`Error fetching time tracker data:  ${response?.message}`);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (
      selectedExamDay ||
      selectedExamDay != '' ||
      selectedShift ||
      selectedShift != '' ||
      selectedExam ||
      selectedExam != ''
    ) {
      setFilter((prev) => ({
        ...prev,
        shift: selectedShift?.value,
        examDay: selectedExamDay?.value,
        selectedExam: selectedExam?.value,
      }));
    }
  }, [selectedShift, selectedExamDay, selectedExam]);

  useEffect(() => {
    getTimeTrackerData();
  }, [filter]);

  const getExams = async () => {
    const response = await MinistryExamApi.getAll();
    if (response.success) {
      const examList = response?.data?.records?.map(({ id, exam_name }) => ({
        value: id,
        label: exam_name,
      }));
      setExams(response?.data?.records ? response?.data?.records : []);
    }
  };

  useEffect(() => {
    getExams();
  }, []);

  useEffect(() => {
    if (selectedExam) {
      const selectedExamId = selectedExam.value;

      const records = exams.find((exam) => {
        return exam.id === selectedExamId;
      });

      const examDays = records?.ExamSchedules?.map((examDay) => {
        return {
          label: moment(examDay.date).format('DD/MM/YYYY'),
          value: moment(examDay.date).format('DD/MM/YYYY'),
        };
      });
      setExamDays(examDays);
    }
  }, [selectedExam]);

  const columns = useMemo(() => [
    {
      Header: 'Date',
      accessor: 'date',
      disableSortBy: true,
      Cell: ({ value }) => {
        return moment(value).format('DD/MM/YYYY');
      },
    },
    {
      Header: 'Shift',
      accessor: 'shift',
      disableSortBy: true,
      Cell: ({ value }) => {
        const normalizedCategory = value.toLowerCase();

        const cellStyle = {
          backgroundColor: shiftColors[normalizedCategory],
          color: 'black',
          fontWeight: 'normal',
          cursor: 'auto',
        };
        return (
          <Button className='btn-sm' variant='primary' style={cellStyle}>
            {value}
          </Button>
        );
      },
    },
    {
      Header: 'Exam',
      accessor: 'exam',
      disableSortBy: true,
    },
    {
      Header: 'Stages Of Exam',
      accessor: 'stages_of_exam',
      disableSortBy: true,
      enableRowSpan: true,
      Cell: ({ value, column }) => {
        const normalizedCategory = value
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '');

        const cellStyle = {
          backgroundColor: categoryColors[normalizedCategory],
          color: 'black',
          fontWeight: 'normal',
          cursor: 'auto',
        };
        return (
          <Button className='btn-sm' variant='primary' style={cellStyle}>
            {value}
          </Button>
        );
      },
    },
    {
      Header: 'Metric',
      accessor: 'metric',
      Cell: ({ value }) => {
        return value.toUpperCase();
      },
      disableSortBy: true,
    },
    {
      Header: 'Time Milestone',
      accessor: 'time_milestone',
      disableSortBy: true,
    },
    {
      Header: 'Count',
      accessor: 'count',
      disableSortBy: true,
      Cell: ({ value }) => {
        return value == 0 ? ' ' : value;
      },
    },
    {
      Header: 'Units of Concern',
      accessor: 'units_of_concern',
      disableSortBy: true,
    },
    {
      Header: 'Remarks of RRB',
      accessor: 'remarks_of_rrb',
      disableSortBy: true,
    },
  ]);

  const examDayDisabled =
    selectedExam === undefined || selectedExam?.value === '';

  const shiftDisabled =
    selectedExamDay === undefined ||
    (Array.isArray(selectedExamDay) && selectedExamDay.length === 0) ||
    selectedExamDay?.value === '';

  useEffect(() => {
    setValue('examDay', []);
    setValue('shift', []);
  }, [selectedExam]);
  useEffect(() => {
    setValue('shift', []);
  }, [selectedExamDay]);

  return (
    <div className='pb-3'>
      <div className='bg-white m-0  p-0 rounded border-1 border'>
        <div
          className='p-0 m-0'
          // containerStyles={{ margin: 0, padding: 0 }}
          // isNodalReport
        >
          <div className='px-3 py-3'>
            <Col md='12'>
              <Row className='d-flex align-items-center '>
                <Col md='6'>
                  <h2 className='m-0'>Time Tracker</h2>
                </Col>
                <Col md='2'>
                  <FormFieldSelect
                    name='exam'
                    control={control}
                    options={[
                      { label: 'Select Exam', value: '' },
                      ...exams.map((exam) => {
                        return {
                          label: exam.exam_name,
                          value: exam.id,
                        };
                      }),
                    ]}
                    error={timeTrackerError?.exam}
                    size='12'
                    placeholder='Select Exam'
                    className='shadow-none m-0'
                    isLabel={false}
                  />
                </Col>
                <Col>
                  <FormFieldSelect
                    name='examDay'
                    control={control}
                    options={examDays}
                    error={timeTrackerError?.examDay}
                    size='12'
                    placeholder='Select Exam Day'
                    className='shadow-none'
                    disabled={examDayDisabled}
                    isLabel={false}
                  />
                </Col>
                <Col md='2'>
                  <FormFieldSelect
                    name='shift'
                    control={control}
                    options={[
                      { label: 'All Shifts', value: '' },
                      { label: 'Shift 1', value: 'shift1' },
                      { label: 'Shift 2', value: 'shift2' },
                      { label: 'Shift 3', value: 'shift3' },
                    ]}
                    error={timeTrackerError?.shift}
                    size='12'
                    placeholder='Select Shift'
                    className='shadow-none'
                    disabled={shiftDisabled}
                    isLabel={false}
                  />
                </Col>
              </Row>
            </Col>
          </div>
          <hr className='m-0' />
          <Row>
            <Col md='12'>
              <DataTable
                cardClass='rounded-0 shadow-none border-0'
                columns={columns}
                data={data}
                totalRecords={totalRecords}
                tableHooks={(hooks) => {
                  hooks.useInstance.push(useRowSpan);
                }}
                enableRowSpan
                defaultPageLength={[25, 50, 100]}
                manual={true}
                align='table-vcenter'
                setFilter={setFilter}
                initialState={initialState}
                isLoading={isLoading}
                placeholder={'Search by Stages of Exam'}
              />
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default TimeTracker;
